import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Divider
} from '@mui/material';
import { noticeAPI } from '../services/api';
import { format, addDays } from 'date-fns';

interface NoticeFormData {
  min_sell_price: string;
  min_price_allowed: string;
  share_quantity_total: string;
  start_date: string;
  end_date: string;
  notes: string;
}

interface FormErrors {
  min_sell_price?: string;
  share_quantity_total?: string;
  start_date?: string;
  end_date?: string;
}

const CreateNotice: React.FC = () => {
  const navigate = useNavigate();
  const today = format(new Date(), 'yyyy-MM-dd');
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');

  const [formData, setFormData] = useState<NoticeFormData>({
    min_sell_price: '',
    min_price_allowed: '',
    share_quantity_total: '',
    start_date: today,
    end_date: tomorrow,
    notes: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Min sell price validation
    const price = parseFloat(formData.min_sell_price);
    if (!formData.min_sell_price || isNaN(price) || price <= 0) {
      newErrors.min_sell_price = 'Minimum sell price must be greater than 0';
    }

    // Share quantity validation
    const quantity = parseInt(formData.share_quantity_total);
    if (!formData.share_quantity_total || isNaN(quantity) || quantity <= 0) {
      newErrors.share_quantity_total = 'Share quantity must be greater than 0';
    }

    // Date validations
    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    } else if (formData.start_date && formData.end_date <= formData.start_date) {
      newErrors.end_date = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof NoticeFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmitClick = () => {
    if (validateForm()) {
      setConfirmOpen(true);
    }
  };

  const handleConfirmCreate = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setSubmitError('');

    try {
      const noticeData = {
        min_sell_price: parseFloat(formData.min_sell_price),
        min_price_allowed: formData.min_price_allowed ? parseFloat(formData.min_price_allowed) : null,
        share_quantity_total: parseInt(formData.share_quantity_total),
        start_date: formData.start_date,
        end_date: formData.end_date,
        notes: formData.notes || null
      };

      await noticeAPI.create(noticeData);
      navigate('/', { state: { success: 'Notice created successfully!' } });
    } catch (err: any) {
      setSubmitError(err.response?.data?.message || 'Failed to create notice');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(num);
  };

  const formatNumber = (value: string) => {
    const num = parseInt(value);
    if (isNaN(num)) return '0';
    return num.toLocaleString();
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Create New Notice
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Issue a new ATM notice to authorize share sales
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/')}>
          Cancel
        </Button>
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError('')}>
          {submitError}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            {/* Pricing Section */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Pricing Parameters
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Minimum Sell Price"
                type="number"
                value={formData.min_sell_price}
                onChange={handleChange('min_sell_price')}
                error={!!errors.min_sell_price}
                helperText={errors.min_sell_price || 'Minimum price Novus is authorized to sell at'}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ step: '0.0001', min: '0' }}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Minimum Price Allowed (Optional)"
                type="number"
                value={formData.min_price_allowed}
                onChange={handleChange('min_price_allowed')}
                helperText="Alternative minimum price, if different"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ step: '0.0001', min: '0' }}
              />
            </Grid>

            {/* Quantity Section */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                Share Quantity
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Total Shares Authorized"
                type="number"
                value={formData.share_quantity_total}
                onChange={handleChange('share_quantity_total')}
                error={!!errors.share_quantity_total}
                helperText={errors.share_quantity_total || 'Maximum shares Novus can sell under this notice'}
                inputProps={{ min: '1' }}
                required
              />
            </Grid>

            {/* Date Section */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                Active Period
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={handleChange('start_date')}
                error={!!errors.start_date}
                helperText={errors.start_date}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: today }}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={handleChange('end_date')}
                error={!!errors.end_date}
                helperText={errors.end_date}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.start_date || today }}
                required
              />
            </Grid>

            {/* Notes Section */}
            <Grid size={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2 }}>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleChange('notes')}
                helperText="Any additional notes or instructions for this notice"
              />
            </Grid>
          </Grid>

          {/* Summary Preview */}
          {(formData.min_sell_price || formData.share_quantity_total) && (
            <Paper variant="outlined" sx={{ mt: 4, p: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Notice Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">Min Sell Price</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(formData.min_sell_price)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">Shares Authorized</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatNumber(formData.share_quantity_total)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">Start Date</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formData.start_date || '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">End Date</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formData.end_date || '-'}
                  </Typography>
                </Grid>
              </Grid>
              {formData.min_sell_price && formData.share_quantity_total && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">
                    Maximum Potential Capital Raise
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(
                      (parseFloat(formData.min_sell_price) * parseInt(formData.share_quantity_total)).toString()
                    )}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          {/* Actions */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate('/')} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitClick}
              disabled={loading}
              size="large"
            >
              {loading ? 'Creating...' : 'Create Notice'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Notice Creation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to create a new ATM notice with the following parameters:
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2"><strong>Min Sell Price:</strong> {formatCurrency(formData.min_sell_price)}</Typography>
            <Typography variant="body2"><strong>Shares Authorized:</strong> {formatNumber(formData.share_quantity_total)}</Typography>
            <Typography variant="body2"><strong>Period:</strong> {formData.start_date} to {formData.end_date}</Typography>
          </Box>
          <DialogContentText sx={{ mt: 2 }}>
            The notice will be created with status <strong>PENDING</strong> and must be manually activated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmCreate} variant="contained" autoFocus>
            Confirm & Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CreateNotice;

